/**
 * CVSS vector parsing.
 *
 * Turns a raw CVSS vector string (v2, v3.x or v4.0) into a list of
 * human-readable metric label/value pairs. The metric-set version is detected
 * from the vector itself (the `CVSS:x.y` prefix, or its absence for v2) — never
 * from any surrounding key — so callers can pass vectors from dynamically-named
 * classification buckets without hardcoding versions.
 *
 * Unknown metrics or values fall back to their raw abbreviations, so an
 * unrecognised or newer vector still renders something sensible.
 */

export interface ParsedCvssMetric {
  /** Metric abbreviation, e.g. "AV". */
  key: string
  /** Human-readable metric name, e.g. "Attack Vector". */
  label: string
  /** Human-readable value, e.g. "Network". */
  value: string
  /** Original `key:value` token. */
  raw: string
}

export interface ParsedCvss {
  /** Detected metric-set version: "2.0", "3.0", "3.1", "4.0", … */
  version: string
  metrics: ParsedCvssMetric[]
}

interface MetricDef {
  label: string
  values: Record<string, string>
}

type VersionMap = Record<string, MetricDef>

const IMPACT_V2 = { N: 'None', P: 'Partial', C: 'Complete' }
const IMPACT_V3 = { H: 'High', L: 'Low', N: 'None' }

const V2_MAP: VersionMap = {
  AV: { label: 'Access Vector', values: { L: 'Local', A: 'Adjacent Network', N: 'Network' } },
  AC: { label: 'Access Complexity', values: { H: 'High', M: 'Medium', L: 'Low' } },
  Au: { label: 'Authentication', values: { M: 'Multiple', S: 'Single', N: 'None' } },
  C: { label: 'Confidentiality Impact', values: IMPACT_V2 },
  I: { label: 'Integrity Impact', values: IMPACT_V2 },
  A: { label: 'Availability Impact', values: IMPACT_V2 },
}

const V3_MAP: VersionMap = {
  AV: { label: 'Attack Vector', values: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' } },
  AC: { label: 'Attack Complexity', values: { L: 'Low', H: 'High' } },
  PR: { label: 'Privileges Required', values: { N: 'None', L: 'Low', H: 'High' } },
  UI: { label: 'User Interaction', values: { N: 'None', R: 'Required' } },
  S: { label: 'Scope', values: { U: 'Unchanged', C: 'Changed' } },
  C: { label: 'Confidentiality Impact', values: IMPACT_V3 },
  I: { label: 'Integrity Impact', values: IMPACT_V3 },
  A: { label: 'Availability Impact', values: IMPACT_V3 },
  E: {
    label: 'Exploit Code Maturity',
    values: { X: 'Not Defined', H: 'High', F: 'Functional', P: 'Proof-of-Concept', U: 'Unproven' },
  },
  RL: {
    label: 'Remediation Level',
    values: { X: 'Not Defined', O: 'Official Fix', T: 'Temporary Fix', W: 'Workaround', U: 'Unavailable' },
  },
  RC: {
    label: 'Report Confidence',
    values: { X: 'Not Defined', C: 'Confirmed', R: 'Reasonable', U: 'Unknown' },
  },
  CR: { label: 'Confidentiality Requirement', values: { X: 'Not Defined', H: 'High', M: 'Medium', L: 'Low' } },
  IR: { label: 'Integrity Requirement', values: { X: 'Not Defined', H: 'High', M: 'Medium', L: 'Low' } },
  AR: { label: 'Availability Requirement', values: { X: 'Not Defined', H: 'High', M: 'Medium', L: 'Low' } },
}

const V4_MAP: VersionMap = {
  AV: { label: 'Attack Vector', values: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' } },
  AC: { label: 'Attack Complexity', values: { L: 'Low', H: 'High' } },
  AT: { label: 'Attack Requirements', values: { N: 'None', P: 'Present' } },
  PR: { label: 'Privileges Required', values: { N: 'None', L: 'Low', H: 'High' } },
  UI: { label: 'User Interaction', values: { N: 'None', P: 'Passive', A: 'Active' } },
  VC: { label: 'Confidentiality Impact to the Vulnerable System', values: IMPACT_V3 },
  VI: { label: 'Integrity Impact to the Vulnerable System', values: IMPACT_V3 },
  VA: { label: 'Availability Impact to the Vulnerable System', values: IMPACT_V3 },
  SC: { label: 'Confidentiality Impact to the Subsequent System', values: IMPACT_V3 },
  SI: { label: 'Integrity Impact to the Subsequent System', values: IMPACT_V3 },
  SA: { label: 'Availability Impact to the Subsequent System', values: IMPACT_V3 },
}

function mapForVersion(version: string): VersionMap {
  const major = version.split('.')[0]
  if (major === '4') return V4_MAP
  if (major === '3') return V3_MAP
  return V2_MAP
}

export function parseCvssVector(vector: string | null | undefined): ParsedCvss | null {
  if (!vector || !vector.trim()) return null

  const tokens = vector
    .trim()
    .split('/')
    .map((t) => t.trim())
    .filter(Boolean)
  if (tokens.length === 0) return null

  let version = '2.0'
  let start = 0
  if (/^CVSS:/i.test(tokens[0])) {
    version = tokens[0].slice(tokens[0].indexOf(':') + 1) || 'unknown'
    start = 1
  }

  const map = mapForVersion(version)
  const metrics: ParsedCvssMetric[] = []

  for (let i = start; i < tokens.length; i++) {
    const token = tokens[i]
    const idx = token.indexOf(':')
    if (idx <= 0) continue
    const key = token.slice(0, idx)
    const rawValue = token.slice(idx + 1)
    if (!rawValue) continue

    const def = map[key]
    metrics.push({
      key,
      label: def?.label ?? key,
      value: def?.values[rawValue] ?? rawValue,
      raw: token,
    })
  }

  return { version, metrics }
}
